 import { ContactLinkTypes, HorecaLocationType, ICartItem, ICartOption, IDeliveryOrderData, IDineInLocation, IDineInOrderData, IOrderParams, IPreOrderData, IReserveOrderData, OrderTypes } from "../Interfaces/componentTypes";
import { ContactLinkResult, IBusinessProfile, IOrderResponse } from "../Interfaces/responseTypes";
import { InvoiceLangTypes, PosPaperSizes } from "../Pages/Business/Restaurant/Components/Dialogs/PrintInvoice/printInvoice-hook";
interface ExtendedNotificationOptions extends NotificationOptions {
  renotify?: boolean;
  vibrate: number[];
  sound?: string; // Custom sound file URL (supported in Chrome/Edge via service workers)
}
export const isBusinessOpen = (profile: IBusinessProfile): boolean => {
  const now = new Date();
  const currentDay = now.getDay() === 0 ? 7 : now.getDay();
  const currentTime = now.getHours() * 60 + now.getMinutes();
  if (profile.free_days?.includes(currentDay)) return false;

  if (profile.is_24) return true;

  if (!profile.start_time || !profile.end_time) return false;

  const [startHour, startMin] = profile.start_time.split(':').map(Number);
  const [endHour, endMin] = profile.end_time.split(':').map(Number);
  const startTime = startHour * 60 + startMin;
  const endTime = endHour * 60 + endMin;

  if (endTime <= startTime) {
    return currentTime >= startTime || currentTime <= endTime;
  }

  return currentTime >= startTime && currentTime <= endTime;
};

export const formatBusinessHours = (profile: IBusinessProfile): string | null => {
  if (profile.is_24) {
    return null;
  }

  if (!profile.start_time || !profile.end_time) {
    return null;
  }

  const formatTime = (time: string) => time.slice(0, 5);
  return `${formatTime(profile.start_time)} – ${formatTime(profile.end_time)}`;
};

export const isBusinessClosed = (profile?: IBusinessProfile | null): boolean => {
  if (!profile) {
    return false;
  }

  return !isBusinessOpen(profile);
};

export const ORDER_TIME_BUFFER_MINUTES = 15;

export const ORDER_TIME_STEP_MINUTES = 5;

export const isValidOrderTimeStep = (time: Date): boolean => {
  const minutes = time.getMinutes();
  return time.getSeconds() === 0
    && time.getMilliseconds() === 0
    && minutes % ORDER_TIME_STEP_MINUTES === 0;
};

export const timeStringToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + (minutes || 0);
};

export const minutesToTimeDate = (totalMinutes: number): Date => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  const normalized = ((totalMinutes % (24 * 60)) + (24 * 60)) % (24 * 60);
  date.setHours(Math.floor(normalized / 60), normalized % 60, 0, 0);
  return date;
};

export const getJsDayAsBusinessDay = (date: Date | string): number => {
  const parsed = typeof date === 'string' ? new Date(`${date}T12:00:00`) : date;
  return parsed.getDay() === 0 ? 7 : parsed.getDay();
};

export const isBusinessFreeDay = (date: Date | string, freeDays: number[] = []): boolean => {
  return freeDays.includes(getJsDayAsBusinessDay(date));
};

export const formatDateForInput = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const getNextOpenDate = (
  profile?: IBusinessProfile | null,
  fromDate: Date = new Date(),
): string => {
  const candidate = new Date(fromDate);
  candidate.setHours(12, 0, 0, 0);

  for (let index = 0; index < 366; index += 1) {
    const dateValue = formatDateForInput(candidate);
    if (!profile || !isBusinessFreeDay(dateValue, profile.free_days)) {
      return dateValue;
    }
    candidate.setDate(candidate.getDate() + 1);
  }

  return formatDateForInput(fromDate);
};

export const getOrderTimeWindow = (
  profile?: IBusinessProfile | null,
): { minMinutes: number | null; maxMinutes: number | null } => {
  if (!profile) {
    return { minMinutes: null, maxMinutes: null };
  }

  if (profile.is_24) {
    return {
      minMinutes: ORDER_TIME_BUFFER_MINUTES,
      maxMinutes: 24 * 60 - ORDER_TIME_BUFFER_MINUTES,
    };
  }

  if (!profile.start_time || !profile.end_time) {
    return { minMinutes: null, maxMinutes: null };
  }

  const startMinutes = timeStringToMinutes(profile.start_time);
  const endMinutes = timeStringToMinutes(profile.end_time);
  const minMinutes = startMinutes + ORDER_TIME_BUFFER_MINUTES;
  let maxMinutes = endMinutes - ORDER_TIME_BUFFER_MINUTES;

  if (endMinutes <= startMinutes) {
    maxMinutes = endMinutes - ORDER_TIME_BUFFER_MINUTES;
    if (maxMinutes < 0) {
      maxMinutes += 24 * 60;
    }
  }

  if (minMinutes > maxMinutes) {
    return { minMinutes: null, maxMinutes: null };
  }

  return { minMinutes, maxMinutes };
};

export const isSameCalendarDay = (left: Date, right: Date): boolean => {
  return (
    left.getFullYear() === right.getFullYear()
    && left.getMonth() === right.getMonth()
    && left.getDate() === right.getDate()
  );
};

export const getOrderTimePickerBounds = (
  profile: IBusinessProfile | null | undefined,
  selectedDate?: string | null,
): { minTime?: Date; maxTime?: Date; isClosedDay: boolean; hasValidWindow: boolean } => {
  if (!selectedDate) {
    return { isClosedDay: false, hasValidWindow: true };
  }

  if (profile && isBusinessFreeDay(selectedDate, profile.free_days)) {
    return { isClosedDay: true, hasValidWindow: false };
  }

  const { minMinutes, maxMinutes } = getOrderTimeWindow(profile);
  if (minMinutes === null || maxMinutes === null) {
    return { isClosedDay: false, hasValidWindow: true };
  }

  let minTime = minutesToTimeDate(minMinutes);
  const maxTime = minutesToTimeDate(maxMinutes);
  const selected = new Date(`${selectedDate}T12:00:00`);
  const today = new Date();

  if (isSameCalendarDay(selected, today)) {
    const now = new Date();
    now.setSeconds(0, 0);
    now.setMilliseconds(0);
    now.setMinutes(now.getMinutes() + 1);

    if (now > minTime) {
      minTime = now;
    }
  }

  return {
    minTime,
    maxTime,
    isClosedDay: false,
    hasValidWindow: minTime <= maxTime,
  };
};

export const getNextOrderDate = (
  profile?: IBusinessProfile | null,
  fromDate: Date = new Date(),
): string => {
  const candidate = new Date(fromDate);
  candidate.setHours(12, 0, 0, 0);

  for (let index = 0; index < 366; index += 1) {
    const dateValue = formatDateForInput(candidate);

    if (!profile) {
      return dateValue;
    }

    if (isBusinessFreeDay(dateValue, profile.free_days)) {
      candidate.setDate(candidate.getDate() + 1);
      continue;
    }

    const { hasValidWindow, isClosedDay } = getOrderTimePickerBounds(profile, dateValue);
    if (!isClosedDay && hasValidWindow) {
      return dateValue;
    }

    candidate.setDate(candidate.getDate() + 1);
  }

  return formatDateForInput(fromDate);
};

export const validateOrderDateTime = (
  date: string | undefined,
  time: Date | null | undefined,
  profile?: IBusinessProfile | null,
): { valid: boolean; reason?: 'closed_day' | 'outside_hours' | 'no_slots' } => {
  if (!profile || !date || !time) {
    return { valid: true };
  }

  if (isBusinessFreeDay(date, profile.free_days)) {
    return { valid: false, reason: 'closed_day' };
  }

  const { minMinutes, maxMinutes } = getOrderTimeWindow(profile);
  if (minMinutes === null || maxMinutes === null) {
    return { valid: true };
  }

  const { hasValidWindow } = getOrderTimePickerBounds(profile, date);
  if (!hasValidWindow) {
    return { valid: false, reason: 'no_slots' };
  }

  const selectedMinutes = time.getHours() * 60 + time.getMinutes();
  if (!isValidOrderTimeStep(time)) {
    return { valid: false, reason: 'outside_hours' };
  }

  if (selectedMinutes < minMinutes || selectedMinutes > maxMinutes) {
    return { valid: false, reason: 'outside_hours' };
  }

  const selected = new Date(`${date}T12:00:00`);
  const today = new Date();
  if (isSameCalendarDay(selected, today)) {
    const nowMinutes = today.getHours() * 60 + today.getMinutes();
    if (selectedMinutes <= nowMinutes) {
      return { valid: false, reason: 'outside_hours' };
    }
  }

  return { valid: true };
};

export const getOrderTimeValidationMessageKey = (
  reason?: 'closed_day' | 'outside_hours' | 'no_slots',
): string => {
  switch (reason) {
    case 'closed_day':
      return 'Visitor.Order_Time.Day_Closed';
    case 'no_slots':
      return 'Visitor.Order_Time.No_Slots';
    default:
      return 'Visitor.Order_Time.Outside_Hours';
  }
};

export const getOrderTimeRequiredMessageKey = (): string => 'Visitor.Order_Time.Required';

export const hasPosTerminal = (value: unknown): boolean => {
  if (value === true || value === 1 || value === '1' || value === 'true' || value === 'True') {
    return true;
  }
  return false;
};

const companyPosTerminalStorageKey = (companyId: number) => `company_${companyId}_pos_terminal`;

export const setCompanyPosTerminalStorage = (companyId: number, enabled: boolean) => {
  localStorage.setItem(companyPosTerminalStorageKey(companyId), enabled ? 'true' : 'false');
};

export const readCompanyPosTerminalFromStorage = (companyId: number): boolean | null => {
  const cached = localStorage.getItem(companyPosTerminalStorageKey(companyId));
  if (cached === 'true') {
    return true;
  }
  if (cached === 'false') {
    return false;
  }
  return null;
};

export const parseCompanyPosTerminal = (
  company?: Partial<{ id?: number; pos_terminal?: boolean | null }> | Record<string, unknown> | null
): boolean | null => {
  if (!company) {
    return null;
  }
  const record = company as Record<string, unknown>;
  const raw = record.pos_terminal ?? record.posTerminal ?? record.has_pos_terminal;

  if (raw !== undefined && raw !== null) {
    return hasPosTerminal(raw);
  }

  const companyId = record.id;
  if (typeof companyId === 'number') {
    return readCompanyPosTerminalFromStorage(companyId);
  }

  return null;
};

export const isPosTerminalEnabled = (value: boolean | null | undefined): boolean => {
  if (value === true) {
    return true;
  }
  return hasPosTerminal(value);
};

export const getCompanyPosTerminal = (
  company?: Partial<{ id?: number; pos_terminal?: boolean | null }> | Record<string, unknown> | null
): boolean => {
  const parsed = parseCompanyPosTerminal(company);
  if (parsed === true) {
    return true;
  }
  if (parsed === false) {
    return false;
  }

  const companyId = company && typeof (company as { id?: unknown }).id === 'number'
    ? (company as { id: number }).id
    : null;

  if (companyId !== null) {
    return readCompanyPosTerminalFromStorage(companyId) === true;
  }

  return false;
};

export const generateContactLink = (
  type: ContactLinkTypes,
  rawValue: string | null
): ContactLinkResult | null => {
  if (!rawValue) return null;

  const trim = (val: string) => val.trim();
  const stripPlus = (val: string) => val.replace(/^\+/, "");
  const stripNonDigits = (val: string) => val.replace(/\D/g, "");
  const stripAt = (val: string) => val.replace(/^@/, "");

  const cleaned = trim(rawValue);

  let href = "";
  let fallback = "";

  switch (type) {
    case "facebook": {
      let url = cleaned;

      const isFullUrl = /^https?:\/\//i.test(url);
      const isDomainOnly = /^facebook\.com\//i.test(url);
      const fullUrl = isFullUrl
        ? url
        : isDomainOnly
          ? `https://${url}`
          : `https://facebook.com/${url}`;

      fallback = fullUrl;
      href = fullUrl; // fb:// deep links are unreliable
      break;
    }

    case "instagram": {
      const username = stripAt(cleaned);
      href = `instagram://user?username=${username}`;
      fallback = `https://instagram.com/${username}`;
      break;
    }

    case "telegram": {
      const username = stripAt(cleaned);
      href = `tg://resolve?domain=${username}`;
      fallback = `https://t.me/${username}`;
      break;
    }

    case "whatsapp": {
      const phone = stripNonDigits(stripPlus(cleaned));
      href = `whatsapp://send?phone=${phone}`;
      fallback = `https://wa.me/${phone}`;
      break;
    }

    case "viber": {
      const phone = stripNonDigits(stripPlus(cleaned));
      href = `viber://chat?number=${phone}`;
      fallback = `https://invite.viber.com/?number=${phone}`;
      break;
    }

    case "twitter": {
      const username = stripAt(cleaned);
      href = `twitter://user?screen_name=${username}`;
      fallback = `https://twitter.com/${username}`;
      break;
    }

    default:
      return null;
  }

  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  const open = () => {
    if (isMobile) {
      const timeout = setTimeout(() => {
        window.open(fallback, "_blank");
      }, 800);

      window.location.href = href;

      window.addEventListener("blur", () => clearTimeout(timeout));
    } else {
      window.open(fallback, "_blank");
    }
  };

  return { href, fallback, open };
};


export const formatPercent = (value: string): string => {
  const num = parseFloat(value);
  if (isNaN(num)) return '';
  const fixed = num.toFixed(2);
  const cleaned = parseFloat(fixed).toString();
  return cleaned;
};

export function getTotalCartItemCount(cart: ICartItem[]): number {
  if (cart.length === 0) {
    return 0;
  }

  return cart.reduce((total: number, item: ICartItem) => {
    const optionCount = Array.isArray(item.option)
      ? item.option.reduce((optionTotal: number, opt: ICartOption) => optionTotal + opt.count, 0)
      : 0;

    return total + optionCount;
  }, 0);
};

export function formatDate(dateInput: string | Date, withTime: boolean = true): string {
  let date: Date;

  if (dateInput instanceof Date) {
    date = dateInput;
  } else if (typeof dateInput === 'string') {
    // Detect DD-MM-YYYY HH:mm
    const match = dateInput.match(/^(\d{2})-(\d{2})-(\d{4})[ T](\d{2}):(\d{2})$/);
    if (match) {
      const [, day, month, year, hour, minute] = match.map(Number);
      date = new Date(year, month - 1, day, hour, minute);
    } else {
      // Let JS try normal parsing (ISO, RFC 2822, etc.)
      date = new Date(dateInput);
    }
  } else {
    return '';
  }

  if (isNaN(date.getTime())) return '';

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  if (!withTime) {
    return `${day}.${month}.${year}`;
  }

  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${day}.${month}.${year} ${hours}:${minutes}`;
};


export function formatTime(dateInput: string | Date): string {
  if (!dateInput) return '';

  const date = new Date(dateInput);
  const hours = date.getHours().toString().padStart(2, '0'); // local hours
  const minutes = date.getMinutes().toString().padStart(2, '0'); // local minutes
  return `${hours}:${minutes}`;
};

const DINE_IN_UNIT_TYPE_LABELS: Record<InvoiceLangTypes, Record<HorecaLocationType, string>> = {
  en: {
    [HorecaLocationType.TABLE]: 'Table',
    [HorecaLocationType.COUNTER]: 'Counter',
    [HorecaLocationType.ROOM]: 'Room',
    [HorecaLocationType.SUITE]: 'Suite',
    [HorecaLocationType.POOL_CHAIR]: 'Pool Chair',
    [HorecaLocationType.CABANA]: 'Cabana',
    [HorecaLocationType.LOUNGE]: 'Lounge',
    [HorecaLocationType.ZONE]: 'Zone',
  },
  ru: {
    [HorecaLocationType.TABLE]: 'Стол',
    [HorecaLocationType.COUNTER]: 'Стойка',
    [HorecaLocationType.ROOM]: 'Комната',
    [HorecaLocationType.SUITE]: 'Люкс',
    [HorecaLocationType.POOL_CHAIR]: 'Шезлонг',
    [HorecaLocationType.CABANA]: 'Кабана',
    [HorecaLocationType.LOUNGE]: 'Лаунж',
    [HorecaLocationType.ZONE]: 'Зона',
  },
  hy: {
    [HorecaLocationType.TABLE]: 'Սեղան',
    [HorecaLocationType.COUNTER]: 'Գծիկ',
    [HorecaLocationType.ROOM]: 'Սենյակ',
    [HorecaLocationType.SUITE]: 'Սյուիտ',
    [HorecaLocationType.POOL_CHAIR]: 'Լողավազանի աթոռ',
    [HorecaLocationType.CABANA]: 'Կաբանա',
    [HorecaLocationType.LOUNGE]: 'Լաոնջ',
    [HorecaLocationType.ZONE]: 'Գոտի',
  },
};

export function resolveDineInLocation(
  orderParams: IOrderParams,
  orderData?: Pick<IDineInOrderData, 'location_type' | 'location_number'> | null,
) {
  const locationType = orderParams.location_type ?? orderData?.location_type;
  const locationNumber = orderParams.location_number ?? orderData?.location_number;

  return { locationType, locationNumber };
}

export function getDineInUnitTypeLabel(
  locationType: HorecaLocationType,
  language?: InvoiceLangTypes,
  t?: (key: string) => string,
): string {
  if (t) {
    return t(`My_Business.My_Services.Dine_In.Unit_Types.${locationType}`);
  }

  if (language) {
    return DINE_IN_UNIT_TYPE_LABELS[language][locationType] ?? locationType;
  }

  return locationType;
}

export function getDineInOrderTitle(
  orderParams: IOrderParams,
  orderData?: Pick<IDineInOrderData, 'location_type' | 'location_number'> | null,
  options: {
    orderLabel?: string;
    language?: InvoiceLangTypes;
    t?: (key: string) => string;
  } = {},
): string | null {
  const { locationType, locationNumber } = resolveDineInLocation(orderParams, orderData);

  if (!locationType || locationNumber == null) {
    return null;
  }

  const unitTypeLabel = getDineInUnitTypeLabel(locationType, options.language, options.t);
  const orderLabel = options.orderLabel
    ?? (options.language
      ? ({ en: 'Order', ru: 'Заказ', hy: 'Պատվեր' } as const)[options.language]
      : 'Order');

  return `${orderLabel} ${unitTypeLabel} ${locationNumber}`;
}

export function getDineInLocationLabel(
  orderParams: IOrderParams,
  orderData?: Pick<IDineInOrderData, 'location_type' | 'location_number'> | null,
  options: {
    language?: InvoiceLangTypes;
    t?: (key: string) => string;
  } = {},
): string | null {
  const { locationType, locationNumber } = resolveDineInLocation(orderParams, orderData);

  if (!locationType || locationNumber == null) {
    return null;
  }

  const unitTypeLabel = getDineInUnitTypeLabel(locationType, options.language, options.t);
  return `${unitTypeLabel} #${locationNumber}`;
}


export function printInvoice(
  order: IOrderResponse,
  language: InvoiceLangTypes,
  paperWidth: PosPaperSizes,
  QR: string
) {
  const orderParams: IOrderParams = JSON.parse(order.order_parameters);
  const orderData: IDeliveryOrderData | IPreOrderData | IReserveOrderData | IDineInOrderData =
    JSON.parse(order.data);
  const baseURL = process.env.REACT_APP_API_KEY;
  const dineInLocationLabel = order.order_type === OrderTypes.DINE_IN
    ? getDineInLocationLabel(orderParams, orderData as IDineInOrderData, { language })
    : null;

  // --- Translations ---
  const t = {
    en: {
      order: "Order",
      orderCreated: "Order Created Date",
      scheduled: "Scheduled Date",
      phone: "Phone",
      address: "Address",
      location: "Location",
      persons: "Persons",
      deliveryFee: "Delivery Fee",
      packageFee: "Package Fee",
      serviceFee: "Service fee",
      total: "TOTAL",
      service: "Service provided by www.mrbyte.am",
      thank: "Thank you!",
      print: "Print",
      close: "Close",
    },
    ru: {
      order: "Заказ",
      orderCreated: "Дата создания заказа",
      scheduled: "Запланированная дата",
      phone: "Телефон",
      address: "Адрес",
      location: "Место",
      persons: "Персон",
      deliveryFee: "Стоимость доставки",
      packageFee: "Стоимость упаковки",
      serviceFee: "Комиссия за обслуживание",
      total: "ИТОГО",
      service: "Сервис предоставлен www.mrbyte.am",
      thank: "Спасибо!",
      print: "Печать",
      close: "Закрыть",
    },
    hy: {
      order: "Պատվեր",
      orderCreated: "Պատվերի ստեղծման օր",
      scheduled: "Նշված օր",
      phone: "Հեռ.",
      address: "Հասցե",
      location: "Տեղ",
      persons: "Անձերի քանակ",
      deliveryFee: "Առաքման վճար",
      packageFee: "Փաթեթավորման վճար",
      serviceFee: "Սպասարկման վճար",
      total: "ԱՄԲՈՂՋ",
      service: "Ծառայությունը տրամադրված է www.mrbyte.am կողմից",
      thank: "Շնորհակալություն!",
      print: "Տպել",
      close: "Փակել",
    },
  }[language];

  // Group cart
  const groupedCart: Record<string, ICartItem[]> = {};
  orderData.cart.forEach((item) => {
    const categoryName =
      (item.category[`name_${language}` as keyof typeof item.category] as string) ||
      item.category.name_en;
    if (!groupedCart[categoryName]) groupedCart[categoryName] = [];
    groupedCart[categoryName].push(item);
  });

  // Helpers
  const win = window.open("", "_blank", `width=420,height=600`);
  if (!win) return;
  const doc = win.document;
  doc.title = "Invoice";
  const isBigPaper = paperWidth === PosPaperSizes.BIG;
  const baseFontSize = isBigPaper ? 16 : 14;
  const headerFontSize = isBigPaper ? 22 : 18;
  const contactsFontSize = isBigPaper ? 15 : 13;
  const categoryFontSize = isBigPaper ? 17 : 15;
  const rowFontSize = isBigPaper ? 17 : 14;
  const totalFontSize = isBigPaper ? 20 : 16;
  const footerFontSize = isBigPaper ? 14 : 13;
  const thankYouFontSize = isBigPaper ? 17 : 15;
  const infoFontSize = isBigPaper ? 15 : 14;
  const noteFontSize = isBigPaper ? 13 : 12;
  const itemQtyMinWidth = isBigPaper ? 36 : 30;
  const itemPriceMinWidth = isBigPaper ? 72 : 60;

  // --- Styles ---
  const style = doc.createElement("style");
  style.textContent = `
    @page { size: ${paperWidth}mm auto; margin: 0; }
    body { font-family: monospace; margin:0; padding:8px; background:#fff; color:#000; font-size:${baseFontSize}px; }
    .paper { width: ${paperWidth}mm; margin:0 auto; padding:6px 8px; border:1px dashed #ddd; position:relative; }
    .header { text-align:center; font-weight:bold; font-size:${headerFontSize}px; margin-bottom:2px; }
    .contacts { text-align:center; font-size:${contactsFontSize}px; margin-bottom:6px; }
    .category { font-weight:bold; border-bottom:1px solid #000; margin:6px 0 4px; font-size:${categoryFontSize}px; }
    .item-row {
      display:flex; justify-content:space-between; align-items:flex-start;
      font-size:${rowFontSize}px; line-height:1.45; margin:2px 0;
    }
    .fee-box {
      border-top:1px solid #000;
      margin-top:6px; padding-top:4px;
      display:flex; justify-content:space-between;
      font-size:${rowFontSize}px; font-weight:bold;
    }
    .item-name { flex:1; text-align:left; word-wrap:break-word; white-space:normal; }
    .item-qty { min-width:${itemQtyMinWidth}px; text-align:center; }
    .item-price { min-width:${itemPriceMinWidth}px; text-align:right; }
    .total-row {
      border-top:2px solid #000;
      margin-top:8px; padding-top:4px;
      display:flex; justify-content:space-between; font-weight:bold;
      font-size:${totalFontSize}px;
    }
    .company-logo {
      display:block;
      max-width:120px;
      max-height:60px;
      margin:8px auto;
      object-fit:contain;
    }
    .footer-box {
      text-align:center; border:1px solid #000; padding:4px; margin-top:8px;
      font-size:${footerFontSize}px; font-weight:bold;
    }
    .thankyou { text-align:center; margin-top:4px; font-weight:bold; font-size:${thankYouFontSize}px; }
    .actions {
      position:fixed; bottom:0; left:0; right:0;
      background:#f8f8f8; padding:10px; display:flex; gap:10px; justify-content:center;
      border-top:1px solid #ccc;
    }
    .actions button {
      flex:1; max-width:120px;
      padding:8px 12px; font-weight:bold;
      border:none; border-radius:6px; cursor:pointer;
      background:#222; color:#fff; transition:0.2s;
    }
    .actions button:hover { background:#555; }
    @media print {
      .actions { display:none; }
      body { padding:0; width:${paperWidth}mm; }
      .paper { border:none; width:${paperWidth}mm; }
    }
  `;
  doc.head.appendChild(style);

  // --- Content ---
  const paperDiv = doc.createElement("div");
  paperDiv.className = "paper";

  // Company header
  if (orderParams.company_logo) {
    const logoImg = doc.createElement("img");
    logoImg.className = "company-logo";
    logoImg.src = `${baseURL}${orderParams.company_logo}`;
    paperDiv.appendChild(logoImg);
  }

  const headerDiv = doc.createElement("div");
  headerDiv.className = "header";
  headerDiv.textContent = orderParams.company_name || "Company";
  paperDiv.appendChild(headerDiv);

  // Contacts
  if (orderParams.company_contacts && orderParams.company_contacts.length > 0) {
    const contactsDiv = doc.createElement("div");
    contactsDiv.className = "contacts";
    contactsDiv.textContent = orderParams.company_contacts.join(" | ");
    paperDiv.appendChild(contactsDiv);
  }

  // Order info
  const orderInfo = doc.createElement("div");
  orderInfo.style.fontSize = `${infoFontSize}px`;
  orderInfo.innerHTML = `
    <div>${t.order} #${order.id}</div>
    <div>${t.orderCreated}: ${formatDate(String(order.created_at))}</div>
    ${orderParams.time
      ? `<div>${t.scheduled}: ${formatDate(String(orderParams.time))}</div>`
      : ""
    }
    ${orderParams.phone_number ? `<div>${t.phone}: ${orderParams.phone_number}</div>` : ""}
    ${orderParams.address ? `<div>${t.address}: ${orderParams.address}</div>` : ""}
    ${dineInLocationLabel ? `<div>${t.location}: ${dineInLocationLabel}</div>` : ""}
    ${orderParams.persons_count || orderParams.persons_count
      ? `<div>${t.persons}: ${orderParams.persons_count ?? orderParams.persons_count ?? "1"}</div>`
      : ""
    }
  `;
  paperDiv.appendChild(orderInfo);

  // Cart
  for (const category in groupedCart) {
    const catDiv = doc.createElement("div");
    catDiv.className = "category";
    catDiv.textContent = category.toUpperCase();
    paperDiv.appendChild(catDiv);

    groupedCart[category].forEach((item) => {
      const itemName =
        (item[`name_${language}` as keyof ICartItem] as string) || item.name_en;
      const quantity =
        (item.option || []).reduce((a, o) => a + (o.count ?? 1), 0) || 1;
      const price = (item.option || []).reduce(
        (a, o) => a + Number(o.price ?? 0) * (o.count ?? 1),
        0
      );

      const itemRow = doc.createElement("div");
      itemRow.className = "item-row";
      itemRow.innerHTML = `
        <span class="item-name">${itemName}</span>
        <span class="item-qty">x${quantity}</span>
        <span class="item-price">${price} ֏</span>
      `;
      paperDiv.appendChild(itemRow);

      if (item.note) {
        const noteDiv = doc.createElement("div");
        noteDiv.style.fontSize = `${noteFontSize}px`;
        noteDiv.style.marginLeft = "8px";
        noteDiv.textContent = `- ${item.note}`;
        paperDiv.appendChild(noteDiv);
      }
    });
  }

  // Delivery + Package Fees
  if ("delivery_cost" in orderData) {
    const row = doc.createElement("div");
    row.className = "fee-box";
    row.innerHTML = `<span>${t.deliveryFee}</span><span>${(orderData as IDeliveryOrderData).delivery_cost
      } ֏</span>`;
    paperDiv.appendChild(row);
  }

  if ("packageFee" in orderData && (orderData as IDeliveryOrderData | IPreOrderData).packageFee) {
    const row = doc.createElement("div");
    row.className = "fee-box";
    row.innerHTML = `<span>${t.packageFee}</span><span>${(orderData as IDeliveryOrderData | IPreOrderData).packageFee
      } ֏</span>`;
    paperDiv.appendChild(row);
  }

  if ("service_fee " in orderData && (orderData as IReserveOrderData).service_fee) {
    const row = doc.createElement("div");
    row.className = "fee-box";
    row.innerHTML = `<span>${t.serviceFee}</span><span>${(orderData as IReserveOrderData).service_fee
      } %</span>`;
    paperDiv.appendChild(row);
  }

  // Total row
  const totalRow = doc.createElement("div");
  totalRow.className = "total-row";
  if (orderData.cart?.length > 0) {
    totalRow.innerHTML = `<span>${t.total}</span><span>${orderData.total_cost} ֏</span>`;
  }
  paperDiv.appendChild(totalRow);

  // --- Logo before footer ---
  if (QR) {
    const qrImg = doc.createElement("img");
    qrImg.className = "company-logo";
    qrImg.src = `${baseURL}${QR}`;
    paperDiv.appendChild(qrImg);
  }

  // Footer
  const footerBox = doc.createElement("div");
  footerBox.className = "footer-box";
  footerBox.textContent = t.service;
  paperDiv.appendChild(footerBox);

  const thankDiv = doc.createElement("div");
  thankDiv.className = "thankyou";
  thankDiv.textContent = t.thank;
  paperDiv.appendChild(thankDiv);

  doc.body.appendChild(paperDiv);

  // Action buttons
  const actionsDiv = doc.createElement("div");
  actionsDiv.className = "actions";
  const printBtn = doc.createElement("button");
  printBtn.textContent = t.print;
  printBtn.onclick = () => win.print();
  const closeBtn = doc.createElement("button");
  closeBtn.textContent = t.close;
  closeBtn.onclick = () => win.close();
  actionsDiv.appendChild(printBtn);
  actionsDiv.appendChild(closeBtn);
  doc.body.appendChild(actionsDiv);
};

// src/utils/notifications.ts
export const requestNotificationPermission = async (): Promise<NotificationPermission> => {
  if (!("Notification" in window)) return "denied";
  if (Notification.permission !== "default") return Notification.permission;
  try {
    const res = await Notification.requestPermission();
    return res;
  } catch {
    return "denied";
  }
};

export const getSWRegistration = async (): Promise<ServiceWorkerRegistration | null> => {
  if (!("serviceWorker" in navigator)) return null;
  try {
    return await navigator.serviceWorker.ready;
  } catch {
    return null;
  }
};

// Show a system notification via the Service Worker (works even when tab is backgrounded)
export const showLocalNotification = (
  title: string,
  body: string,
  swRegistration: ServiceWorkerRegistration
) => {
  const options: ExtendedNotificationOptions = {
    body,
    icon: "/icon.png",
    vibrate: [200, 100, 200],
    renotify: true, // ✅ now TypeScript won't complain
    tag: "notification-tag",
    silent: false, // Allow sound
    sound: "/Sounds/notify.mp3" // Custom sound file - plays even when tab is hidden!
  };

  swRegistration.showNotification(title, options);
};

export const isNotificationPermissionGranted = (): boolean => {
  return "Notification" in window && Notification.permission === "granted";
};

export const isNotificationPermissionDenied = (): boolean => {
  return "Notification" in window && Notification.permission === "denied";
};

export const getCompanyTitleSlug = (companyTitle: string): string =>
  companyTitle.replace(/\s+/g, '');

export const buildMenuQrLink = (companyTitle: string, companyId: number): string => {
  const companyTitleSlug = getCompanyTitleSlug(companyTitle);
  return `https://www.mrbyte.am/${companyTitleSlug}/menu/${companyId}`;
};

export const buildDineInQrLink = (
  companyTitle: string,
  companyId: number,
  locationType: string,
  locationNumber: number,
): string => `${buildMenuQrLink(companyTitle, companyId)}/${locationType}/${locationNumber}`;

const DINE_IN_LOCATION_STORAGE_PREFIX = 'dine_in_location_';

export const parseDineInLocationParams = (
  locationType?: string,
  locationNumber?: string,
): IDineInLocation | null => {
  if (!locationType || !locationNumber) {
    return null;
  }

  const normalizedType = locationType.toLowerCase();

  if (!Object.values(HorecaLocationType).includes(normalizedType as HorecaLocationType)) {
    return null;
  }

  const number = Number(locationNumber);
  if (!Number.isInteger(number) || number <= 0) {
    return null;
  }

  return {
    location_type: normalizedType as HorecaLocationType,
    location_number: number,
  };
};

export const persistDineInLocation = (
  companyId: number,
  location: IDineInLocation,
): void => {
  sessionStorage.setItem(
    `${DINE_IN_LOCATION_STORAGE_PREFIX}${companyId}`,
    JSON.stringify(location),
  );
};

export const getPersistedDineInLocation = (
  companyId: number,
): IDineInLocation | null => {
  const stored = sessionStorage.getItem(`${DINE_IN_LOCATION_STORAGE_PREFIX}${companyId}`);
  if (!stored) {
    return null;
  }

  try {
    const parsed = JSON.parse(stored) as IDineInLocation;
    return parseDineInLocationParams(parsed.location_type, String(parsed.location_number));
  } catch {
    return null;
  }
};

export const clearPersistedDineInLocation = (companyId: number): void => {
  sessionStorage.removeItem(`${DINE_IN_LOCATION_STORAGE_PREFIX}${companyId}`);
};
